import type {
  AccessListItem,
  AccessPage,
  GroupFile,
  GroupItemList,
  GroupItemTypes,
  GroupMember,
  NamedRow,
} from '@app/application/user-management/types'
import type {
  AccessListState,
  AccessListView,
} from '@app/application/user-management/utils/access-lists'

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import {
  create,
  destroy,
  fetchItems,
  get,
  update,
} from '@app/application/user-management/api/groups'
import {
  GROUP_ITEM_LISTS,
  GROUP_ITEM_REQUEST_KEYS,
  GROUP_PANEL_REQUEST_KEY,
  STALE_ACCESS_LIST_REQUEST_REASON,
  STALE_GROUP_PANEL_REQUEST_REASON,
  UNPROCESSABLE_ENTITY,
} from '@app/application/user-management/constants'
import { abort } from '@app/adapters/abort'
import { isValidationErrorBody } from '@app/application/user-management/predicates/is-validation-error-body'
import { startLatestRequest } from '@app/application/user-management/utils/list-query'
import {
  emptyList,
  loadAccessPage,
  removeRow,
  viewOf,
} from '@app/application/user-management/utils/access-lists'
import { isResponseError } from '@app/utils/predicates'
import useI18n from '@app/shared/vue/composables/use-I18n'

const validationErrors = (error: unknown): Record<string, string[]> | null =>
  isResponseError(error) &&
  error.status === UNPROCESSABLE_ENTITY &&
  isValidationErrorBody(error.body)
    ? error.body.errors
    : null

type GroupItemLists = {
  [L in GroupItemList]: AccessListState<GroupItemTypes[L]>
}

type GroupItemViews = {
  [L in GroupItemList]: AccessListView<GroupItemTypes[L]>
}

const emptyItemLists = (saved: boolean): GroupItemLists => ({
  members: emptyList(saved),
  files: emptyList(saved),
})

const addedIds = (list: AccessListState<NamedRow>): number[] =>
  list.added.map((row) => row.id)

const removedIds = (list: AccessListState<NamedRow>): number[] =>
  list.removed.map((row) => row.id)

export const useGroupPanelStore = defineStore('groupPanel', () => {
  const I18n = useI18n()

  const isOpen = ref(false)
  const openGroupId = ref<number | null>(null)
  const name = ref('')
  const loadedName = ref('')
  const lists = ref<GroupItemLists>(emptyItemLists(false))
  const loading = ref(false)
  const loadError = ref(false)
  const saving = ref(false)
  const deleting = ref(false)
  const confirmingDelete = ref(false)
  const saveError = ref<string | null>(null)
  const nameErrors = ref<string[]>([])

  const creating = computed(() => isOpen.value && openGroupId.value === null)

  const isDirty = computed(
    () =>
      name.value !== loadedName.value ||
      Object.values(lists.value).some(
        (list) => list.added.length > 0 || list.removed.length > 0
      )
  )

  const itemViews = computed<GroupItemViews>(() => ({
    members: viewOf(lists.value.members),
    files: viewOf(lists.value.files),
  }))

  function abortLists(): void {
    GROUP_ITEM_LISTS.forEach((list) =>
      abort(GROUP_ITEM_REQUEST_KEYS[list], STALE_ACCESS_LIST_REQUEST_REASON)
    )
  }

  function reset(saved: boolean): void {
    abortLists()
    name.value = ''
    loadedName.value = ''
    lists.value = emptyItemLists(saved)
    nameErrors.value = []
    saveError.value = null
    loading.value = false
    loadError.value = false
    confirmingDelete.value = false
  }

  // A 422 answers `{errors: {<attribute>: [<full message>]}}`. The panel shows
  // name errors under the field and joins all other messages into `saveError`.
  function reportSaveFailure(error: unknown): void {
    const errors = validationErrors(error)

    if (!errors) {
      saveError.value = I18n.t('group.panel.save_error')

      return
    }

    nameErrors.value = errors.name ?? []

    const others = Object.entries(errors)
      .filter(([attribute]) => attribute !== 'name')
      .flatMap(([, messages]) => messages)

    if (others.length) saveError.value = others.join(' ')
    if (!nameErrors.value.length && !others.length) {
      saveError.value = I18n.t('group.panel.save_error')
    }
  }

  async function open(id: number): Promise<void> {
    const isStale = startLatestRequest(
      GROUP_PANEL_REQUEST_KEY,
      STALE_GROUP_PANEL_REQUEST_REASON
    )

    reset(true)
    isOpen.value = true
    openGroupId.value = id
    loading.value = true

    try {
      const details = await get(id)

      if (isStale()) return

      if (details) {
        name.value = details.name
        loadedName.value = details.name
        GROUP_ITEM_LISTS.forEach((list) => void searchItems(list, ''))
      }
    } catch {
      if (!isStale()) loadError.value = true
    } finally {
      if (!isStale()) loading.value = false
    }
  }

  function openNew(): void {
    abort(GROUP_PANEL_REQUEST_KEY, STALE_GROUP_PANEL_REQUEST_REASON)
    reset(false)
    isOpen.value = true
    openGroupId.value = null
  }

  function close(): void {
    abort(GROUP_PANEL_REQUEST_KEY, STALE_GROUP_PANEL_REQUEST_REASON)
    reset(false)
    isOpen.value = false
    openGroupId.value = null
  }

  function searchItems(list: GroupItemList, query: string): Promise<void> {
    return fetchItemPage(list, query, 1)
  }

  // The next page belongs to the query the loaded rows answer, so a search in
  // flight wins.
  function loadMoreItems(list: GroupItemList): Promise<void> {
    const state = lists.value[list]

    if (state.loading) return Promise.resolve()

    return fetchItemPage(list, state.query, state.page + 1)
  }

  function fetchItemPage<L extends GroupItemList>(
    list: L,
    query: string,
    page: number
  ): Promise<void> {
    const id = openGroupId.value
    const state: GroupItemLists[L] = lists.value[list]

    return loadAccessPage(
      state,
      GROUP_ITEM_REQUEST_KEYS[list],
      query,
      page,
      id === null
        ? null
        : (signal): Promise<AccessPage<GroupItemTypes[L]> | null> =>
            fetchItems(id, list, query, page, signal)
    )
  }

  // The picker leaves out the rows the group holds, so only the loaded and
  // added rows are left to check.
  function addItem<L extends GroupItemList>(
    list: L,
    item: GroupItemTypes[L]
  ): void {
    const state: GroupItemLists[L] = lists.value[list]

    if ([...state.added, ...state.rows].some((row) => row.id === item.id)) {
      return
    }

    state.added.unshift({ ...item })
  }

  function addMember(member: GroupMember): void {
    addItem('members', member)
  }

  function removeMember(id: AccessListItem['key']): void {
    removeRow(lists.value.members, id)
  }

  function addFile(file: GroupFile): void {
    addItem('files', file)
  }

  function removeFile(id: AccessListItem['key']): void {
    removeRow(lists.value.files, id)
  }

  async function save(): Promise<boolean> {
    const id = openGroupId.value

    nameErrors.value = []
    saveError.value = null
    saving.value = true

    const { members, files } = lists.value
    const group = {
      name: name.value,
      addedUserIds: addedIds(members),
      removedUserIds: removedIds(members),
      addedCompanyIds: addedIds(files),
      removedCompanyIds: removedIds(files),
    }

    try {
      if (id === null) {
        await create(group)
      } else {
        await update(id, group)
      }

      return true
    } catch (error) {
      reportSaveFailure(error)

      return false
    } finally {
      saving.value = false
    }
  }

  async function remove(): Promise<boolean> {
    const id = openGroupId.value

    if (id === null) return false

    deleting.value = true

    try {
      await destroy(id)

      return true
    } catch {
      saveError.value = I18n.t('group.panel.delete_error')

      return false
    } finally {
      confirmingDelete.value = false
      deleting.value = false
    }
  }

  return {
    addFile,
    addMember,
    close,
    confirmingDelete,
    creating,
    deleting,
    isDirty,
    isOpen,
    itemViews,
    loadError,
    loadMoreItems,
    loading,
    name,
    nameErrors,
    open,
    openGroupId,
    openNew,
    remove,
    removeFile,
    removeMember,
    save,
    saveError,
    saving,
    searchItems,
  }
})
